<script setup lang="ts">
import { ref, computed, inject, watch, onMounted, nextTick } from 'vue';
import { configuratorStateKey } from '../ProductConfiguratorKeys';
import { injectionKeys as litiumKeys } from '@motillo/drapejs-litium';
import { inputFocusKey } from '../ProductConfiguratorKeys';
import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import QuantitySelectorRowDropdown from './QuantitySelectorRowDropdown.vue';

const { page, formatPrice } = useContext();
const { configuratorState } = inject(configuratorStateKey);
const { websiteText } = useWebsiteTexts();
const emit = defineEmits(['updateQuantity']);
const toLitiumMediaUrl = inject(litiumKeys.toLitiumMediaUrlKey, () => '');
const { updateFocusState } = inject(inputFocusKey);

const props = defineProps({
  quantity: {
    type: Number,
    required: true
  },
  variant: {
    type: Object,
    required: true
  },
  hasDecoration: {
    type: Boolean,
    default: false
  },
  variantSupported: {
    type: Boolean,
    default: false
  },
  selectedDecorationName: {
    type: String,
    default: ''
  },
  isGiftCard: {
    type: Boolean,
    default: false
  }
});

const inputVal = ref(null);
const oldInputVal = ref(null);
const inputNumber = computed(() => {
  if (typeof inputVal.value === 'string') return 0;
  if (
    !Number.isNaN(inputVal.value) &&
    !Number.isNaN(parseInt(inputVal.value, 10))
  ) {
    return Math.min(
      maxQuantityInput.value,
      Math.abs(parseInt(inputVal.value, 10))
    );
  }
  return 0;
});
const shownArticleNumber = computed(() => {
  if (typeof props.variant?.fields?.NAVArticleNumber === 'string'
    && props.variant.fields.NAVArticleNumber.trim() !== ''
  ) {
    return props.variant.fields.NAVArticleNumber;
  }

  return props.variant?.articleNumber;
});

const variantImage = computed(() => {
  if (page.value.dataJson?.datajson?.product?.isGiftcard) {
    const designAddon =
      configuratorState.value.selections?.selectedAddons?.find(
        (a) => a.id == 'Addon-Design'
      );
    const giftCardImage = props.variant?.images?.find((i) =>
      i.filename.includes(designAddon?.variant?.id)
    );
    if (!!giftCardImage) {
      return toLitiumMediaUrl(giftCardImage.id, { maxWidth: 42 });
    }
    const defaultImage = props.variant?.images?.find((i) =>
      i.filename.includes('default')
    );
    if (!!defaultImage) {
      return toLitiumMediaUrl(defaultImage.id, { maxWidth: 42 });
    }
    return '';
  } else {
    const productImage = props.variant.images[0];
    if (!!productImage) {
      return toLitiumMediaUrl(productImage.id, { maxWidth: 42 });
    }
    return '';
  }
});

const variantName = computed(() => {
  return props.variant.fields?.Color?.[0]?.name || '';
});

const variantDiscount = computed(() => {
  const { campaignPrice } = props.variant;
  if (!campaignPrice) return 0;
  return `(-${formatPrice(
    Math.abs(
      (campaignPrice / (props.variant.price || props.variant.originalPrice)) *
        -100
    ),
    undefined,
    '',
    '',
    'decimal'
  )}%)`;
});

const stockData = computed(() => {
  return props.hasDecoration
    ? props.variant.printableStock
    : props.variant.stock;
});

const mappedStockData = computed(() => {
  const stock = [];
  const variantStock = stockData.value;

  if (
    variantStock.currentStock.amount === 0 &&
    variantStock.incomingStock.length === 0
  ) {
    stock.push({
      text: `${websiteText('productdetailspage_outofstock').value}`,
      icon: 'out',
      id: variantStock.currentStock.systemId,
      quantity: 0,
      totalQuantity: 0,
      type: 'option'
    });
  }

  if (variantStock.currentStock.amount !== 0) {
    stock.push({
      text: `${websiteText('global__in_stock').value}: ${
        variantStock.currentStock.amount
      }`,
      icon: 'available',
      id: variantStock.currentStock.systemId,
      quantity: variantStock.currentStock.amount,
      totalQuantity: variantStock.currentStock.amount,
      type: 'option'
    });
  }

  if (variantStock.incomingStock.length > 0) {
    stock.push({
      text: websiteText('productdetailspage_totalstock').value,
      id: 1,
      quantity: 0,
      type: 'headline'
    });

    variantStock.incomingStock.forEach((s) => {
      const date = new Date(s.date);
      const totalQuantity =
        variantStock.currentStock.amount +
        s.amount +
        variantStock.incomingStock.reduce((acc, cur) => {
          const incomingDate = new Date(cur.date);
          return incomingDate < date ? acc + cur.amount : acc;
        }, 0);

      stock.push({
        text: `${date.getDate()}/${date.getMonth() + 1}: ${totalQuantity}`,
        icon: 'incoming',
        id: s.systemId,
        quantity: s.amount,
        totalQuantity,
        type: 'option'
      });
    });
  }

  return stock;
});

const maxQuantityInput = computed(() => {
  return mappedStockData.value.reduce((acc, curr) => acc + curr.quantity, 0);
});

const outOfStock = computed(
  () => maxQuantityInput.value === 0 || !props.variantSupported
);

function updateFocus(event: FocusEvent) {
  if (event.type === 'focus') {
    updateFocusState(true);
  }
  if (event.type === 'blur') {
    updateFocusState(false);
  }
}

function updateQuantity() {
  inputVal.value = inputNumber.value === 0 ? '' : inputNumber.value;
  emit('updateQuantity', {
    quantity: inputNumber.value,
    id: props.variant.systemId
  });
}

onMounted(() => {
  nextTick(() => {
    inputVal.value = !inputVal.value && props.quantity ? props.quantity : null;
  });
});

watch(outOfStock, (val) => {
  if (val) {
    updateQuantity();
  }
});

const isVariantSupported = computed(() => props.variantSupported);
watch(isVariantSupported, (val) => {
  if (!val) {
    oldInputVal.value = inputVal.value;
    inputVal.value = null;
    updateQuantity();
  } else if (oldInputVal.value) {
    inputVal.value = oldInputVal.value;
    updateQuantity();
  }
});

watch(configuratorState.value, (val) => {
  if (!val.sumQuantity) {
    inputVal.value = null;
  }
});

const notAvailableText = computed(() => {
  if (isVariantSupported.value) {
    return '';
  }

  if (props.selectedDecorationName === '') {
    return '';
  }

  return websiteText('decoration__not_available', {
    decoration: props.selectedDecorationName,
    variant: variantName.value
  }).value;
});
</script>

<template>
  <div class="quantity-selector-row">
    <div class="quantity-selector-row__container">
      <div class="quantity-selector-row__image-container">
        <div class="quantity-selector-row__spacer"></div>
        <div class="quantity-selector-row__overlay"></div>
        <img
          class="quantity-selector-row__image"
          :src="variantImage"
          alt="Variant image"
        />
      </div>
      <div class="quantity-selector-row__details">
        <div class="quantity-selector-row__text">
          <div v-if="variantName" class="quantity-selector-row__name">
            {{ variantName }}
          </div>
          <div v-if="variantDiscount" class="quantity-selector-row__discount">
            {{ variantDiscount }}
          </div>
          <div
            v-if="shownArticleNumber"
            class="quantity-selector-row__divider"
          ></div>
          <div
            v-if="shownArticleNumber"
            class="quantity-selector-row__article-number"
          >
            {{ shownArticleNumber }}
          </div>
        </div>
        <input
          v-model="inputVal"
          @keyup="updateQuantity"
          class="quantity-selector-row__input"
          type="number"
          min="0"
          :disabled="outOfStock"
          :max="maxQuantityInput"
          @focus="updateFocus"
          @blur="updateFocus"
        />
        <QuantitySelectorRowDropdown
          class="quantity-selector-row__dropdown"
          :quantity="inputNumber"
          :stock-data="mappedStockData"
          :isGiftCard="isGiftCard"
        >
        </QuantitySelectorRowDropdown>
      </div>
    </div>
    <div class="quantity-selector-row__warning" v-if="notAvailableText">
      <img
        class="quantity-selector-row__warning_icon"
        src="../../../static/icons/info.svg"
      />
      {{ notAvailableText }}
    </div>
  </div>
</template>

<style>
.quantity-selector-row {
  display: flex;
  flex-direction: column;
  margin-top: 15px;
}

.quantity-selector-row__container {
  display: flex;
}

.quantity-selector-row__image-container {
  width: 42px;
  position: relative;
  flex-shrink: 0;
  margin-right: 6px;
  overflow: hidden;
}

.quantity-selector-row__spacer {
  padding-top: 121%;
}

.quantity-selector-row__overlay {
  z-index: 10;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0.8;
  background-color: #efefef;
  mix-blend-mode: darken;
  pointer-events: none;
}

.quantity-selector-row__image {
  position: absolute;
  top: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  margin: auto;
}

.quantity-selector-row__details {
  width: 100%;
  display: grid;
  grid-template-columns: 30% 70%;
  grid-template-areas:
    'a a'
    'b c';
  column-gap: 8px;
  row-gap: 8px;
}

.quantity-selector-row__text {
  display: flex;
  line-height: 17px;
  font-size: 12px;
  color: var(--color-text-darker);
  grid-area: a;
}

.quantity-selector-row__name {
  font-weight: bold;
}

.quantity-selector-row__discount {
  margin-left: 6px;
  color: var(--color-feedback-error);
}

.quantity-selector-row__divider {
  margin-left: 6px;
  height: 10px;
  align-self: center;
  border-left: 1px solid var(--color-neutrals-60);
}

.quantity-selector-row__article-number {
  margin-left: 6px;
}

.quantity-selector-row__quantity {
  display: flex;
  width: 100%;
}

.quantity-selector-row__input[type='number'] {
  min-width: 84px;
  height: 30px;
  -moz-appearance: textfield;
  grid-area: b;
}

.quantity-selector-row__input[type='number']:disabled {
  background-color: var(--color-neutrals-10);
}

.quantity-selector-row__input[type='number']::-webkit-outer-spin-button,
.quantity-selector-row__input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.quantity-selector-row__dropdown {
  grid-area: c;
}

.quantity-selector-row__warning {
  display: flex;
  align-items: flex-start;
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  color: #5379ff;
}

.quantity-selector-row__warning_icon {
  width: 20px;
  height: 20px;
  padding-right: 4px;
  margin-top: 3px;
}

@media (max-width: 379px) {
  .quantity-selector-row__image-container {
    width: 68px;
  }

  .quantity-selector-row__details {
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'b a'
      'c c';
    column-gap: 10px;
    row-gap: 4px;
  }

  .quantity-selector-row__text {
    align-self: center;
    display: flex;
    flex-wrap: wrap;
    flex: 0 0 100%;
  }

  .quantity-selector-row__divider {
    display: none;
  }

  .quantity-selector-row__article-number {
    margin-left: 0;
    flex: 0 0 100%;
  }

  .quantity-selector-row__input[type='number'] {
    max-width: none;
    height: 40px;
  }

  .quantity-selector-row-dropdown__stock {
    height: 40px;
  }

  .quantity-selector-row-dropdown__stock::before {
    top: 13px;
  }
}
</style>
